import React, { useCallback, useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import TuneIcon from "@mui/icons-material/Tune";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";

type SupportedMimeType = "image/png" | "image/jpeg" | "image/webp";
type ItemStatus = "ready" | "processing" | "done" | "error";
type CropAspectPreset = "free" | "original" | "square" | "4:3" | "16:9" | "3:4";

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropState {
  enabled: boolean;
  rect: CropRect;
  aspectPreset: CropAspectPreset;
}

interface LivePreviewState {
  url: string | null;
  ownsUrl: boolean;
  size: number | null;
  width: number | null;
  height: number | null;
  mimeType: SupportedMimeType | null;
  metadataRemoved: boolean;
  generating: boolean;
  error: string | null;
}

interface BrowserSupportState {
  canvas: boolean;
  toBlob: boolean;
  fileApi: boolean;
  dragDrop: boolean;
  createImageBitmap: boolean;
  workers: boolean;
  webpExport: boolean;
}

interface ImageItem {
  id: string;
  file: File;
  originalUrl: string;
  originalName: string;
  originalType: SupportedMimeType;
  originalSize: number;
  originalWidth: number;
  originalHeight: number;
  status: ItemStatus;
  processedUrl?: string;
  processedBlob?: Blob;
  processedName?: string;
  processedType?: SupportedMimeType;
  processedSize?: number;
  processedWidth?: number;
  processedHeight?: number;
  reductionPercent?: number;
  metadataRemoved?: boolean;
  error?: string;
}

const ACCEPTED_TYPES: SupportedMimeType[] = [
  "image/png",
  "image/jpeg",
  "image/webp",
];
const DEFAULT_QUALITY = 82;
const MAX_FILES = 50;
const MIN_CROP_PERCENT = 5;
const DEFAULT_CROP_RECT: CropRect = {
  x: 10,
  y: 10,
  width: 80,
  height: 80,
};

const compressionFaqItems = [
  {
    question: "Does the image compression tool upload photos to a server?",
    answer:
      "No. Compression, resizing, format conversion and ZIP packaging all happen locally in your browser. Images never leave your device.",
  },
  {
    question: "Which image formats are supported?",
    answer:
      "The tool accepts PNG, JPG/JPEG and WebP input files. Output can be exported as PNG, JPG or WebP depending on browser support.",
  },
  {
    question: "Does processing remove metadata?",
    answer:
      "Yes. When the image is re-rendered through canvas, EXIF and similar metadata are stripped from the processed export.",
  },
  {
    question: "Can I compress multiple images at once?",
    answer:
      "Yes. You can upload a batch of images, process them sequentially in the browser and download the results individually or as a ZIP archive.",
  },
] as const;

const compressionUseCases = [
  "Compress website images before uploading to static hosting",
  "Convert PNG screenshots into smaller JPG or WebP files",
  "Resize product, blog and social images for faster page loads",
  "Strip metadata before sharing images publicly",
  "Batch-process multiple assets and download them as a ZIP",
  "Prepare lightweight image bundles for Cloudflare Pages, Netlify or GitHub Pages",
];

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let index = 0;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  return `${value.toFixed(value >= 100 || index === 0 ? 0 : 2)} ${units[index]}`;
};

const formatPercent = (value: number) => `${Math.max(value, 0).toFixed(1)}%`;

const getFormatLabel = (mimeType: SupportedMimeType) => {
  switch (mimeType) {
    case "image/png":
      return "PNG";
    case "image/jpeg":
      return "JPG";
    case "image/webp":
      return "WebP";
    default:
      return mimeType;
  }
};

const getExtensionForMimeType = (mimeType: SupportedMimeType) => {
  switch (mimeType) {
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    case "image/webp":
      return "webp";
    default:
      return "img";
  }
};

const getProcessedFileName = (
  fileName: string,
  mimeType: SupportedMimeType,
) => {
  const baseName = fileName.replace(/\.[^.]+$/, "");
  return `${baseName}.${getExtensionForMimeType(mimeType)}`;
};

const createObjectUrl = (blob: Blob) => URL.createObjectURL(blob);

const revokeUrl = (url?: string) => {
  if (url) {
    URL.revokeObjectURL(url);
  }
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const loadImageElement = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load the image."));
    image.src = src;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: SupportedMimeType,
  quality?: number,
) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("Image export is not supported in this browser."));
      },
      mimeType,
      quality,
    );
  });

const getNumericInputValue = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const getCropAspectValue = (
  preset: CropAspectPreset,
  originalRatio: number,
) => {
  switch (preset) {
    case "free":
      return null;
    case "original":
      return originalRatio;
    case "square":
      return 1;
    case "4:3":
      return 4 / 3;
    case "16:9":
      return 16 / 9;
    case "3:4":
      return 3 / 4;
    default:
      return null;
  }
};

const normalizeCropRect = (rect: CropRect): CropRect => {
  const width = clamp(rect.width, MIN_CROP_PERCENT, 100);
  const height = clamp(rect.height, MIN_CROP_PERCENT, 100);
  const x = clamp(rect.x, 0, 100 - width);
  const y = clamp(rect.y, 0, 100 - height);

  return { x, y, width, height };
};

const getCropRectInPixels = (
  rect: CropRect,
  imageWidth: number,
  imageHeight: number,
) => {
  const safeRect = normalizeCropRect(rect);
  return {
    x: Math.round((safeRect.x / 100) * imageWidth),
    y: Math.round((safeRect.y / 100) * imageHeight),
    width: Math.max(1, Math.round((safeRect.width / 100) * imageWidth)),
    height: Math.max(1, Math.round((safeRect.height / 100) * imageHeight)),
  };
};

const getDefaultCropState = (): CropState => ({
  enabled: false,
  rect: DEFAULT_CROP_RECT,
  aspectPreset: "free",
});

const getSourceDimensions = (
  width: number,
  height: number,
  cropState: CropState,
) => {
  if (!cropState.enabled) {
    return { width, height };
  }

  const cropRect = getCropRectInPixels(cropState.rect, width, height);
  return { width: cropRect.width, height: cropRect.height };
};

const applyAspectToCropRect = (
  rect: CropRect,
  preset: CropAspectPreset,
  originalWidth: number,
  originalHeight: number,
) => {
  const safeRect = normalizeCropRect(rect);
  const originalRatio = originalWidth / originalHeight;
  const targetAspect = getCropAspectValue(preset, originalRatio);

  if (!targetAspect) {
    return safeRect;
  }

  const pixelWidth = (safeRect.width / 100) * originalWidth;
  const pixelHeight = (safeRect.height / 100) * originalHeight;

  let nextPixelWidth = pixelWidth;
  let nextPixelHeight = pixelHeight;

  if (pixelWidth / pixelHeight > targetAspect) {
    nextPixelWidth = pixelHeight * targetAspect;
  } else {
    nextPixelHeight = pixelWidth / targetAspect;
  }

  const nextRect = normalizeCropRect({
    x: safeRect.x,
    y: safeRect.y,
    width: (nextPixelWidth / originalWidth) * 100,
    height: (nextPixelHeight / originalHeight) * 100,
  });

  return normalizeCropRect({
    ...nextRect,
    x: clamp(safeRect.x, 0, 100 - nextRect.width),
    y: clamp(safeRect.y, 0, 100 - nextRect.height),
  });
};

const getTargetDimensions = ({
  originalWidth,
  originalHeight,
  resizeEnabled,
  targetWidth,
  targetHeight,
  preserveAspectRatio,
}: {
  originalWidth: number;
  originalHeight: number;
  resizeEnabled: boolean;
  targetWidth: string;
  targetHeight: string;
  preserveAspectRatio: boolean;
}) => {
  if (!resizeEnabled) {
    return { width: originalWidth, height: originalHeight };
  }

  const widthInput = getNumericInputValue(targetWidth);
  const heightInput = getNumericInputValue(targetHeight);

  if (!widthInput && !heightInput) {
    return { width: originalWidth, height: originalHeight };
  }

  if (!preserveAspectRatio) {
    return {
      width: widthInput ?? originalWidth,
      height: heightInput ?? originalHeight,
    };
  }

  const ratio = originalWidth / originalHeight;

  if (widthInput && heightInput) {
    const scale = Math.min(
      widthInput / originalWidth,
      heightInput / originalHeight,
    );
    return {
      width: Math.max(1, Math.round(originalWidth * scale)),
      height: Math.max(1, Math.round(originalHeight * scale)),
    };
  }

  if (widthInput) {
    return {
      width: widthInput,
      height: Math.max(1, Math.round(widthInput / ratio)),
    };
  }

  if (heightInput) {
    return {
      width: Math.max(1, Math.round(heightInput * ratio)),
      height: heightInput,
    };
  }

  return { width: originalWidth, height: originalHeight };
};

const detectBrowserSupport = async (): Promise<BrowserSupportState> => {
  const canvas =
    typeof document !== "undefined" ? document.createElement("canvas") : null;
  let webpExport = false;

  if (canvas) {
    try {
      webpExport = canvas.toDataURL("image/webp").startsWith("data:image/webp");
    } catch (_error) {
      webpExport = false;
    }
  }

  return {
    canvas: Boolean(canvas && canvas.getContext("2d")),
    toBlob: Boolean(canvas && typeof canvas.toBlob === "function"),
    fileApi:
      typeof window !== "undefined" &&
      typeof window.File !== "undefined" &&
      typeof window.FileReader !== "undefined" &&
      typeof window.URL !== "undefined",
    dragDrop:
      typeof window !== "undefined" &&
      "DataTransfer" in window &&
      "draggable" in document.createElement("div"),
    createImageBitmap:
      typeof window !== "undefined" && "createImageBitmap" in window,
    workers:
      typeof window !== "undefined" && typeof window.Worker !== "undefined",
    webpExport,
  };
};

const shouldTransformImage = ({
  item,
  cropState,
  removeMetadata,
  resizeEnabled,
  outputFormat,
  quality,
}: {
  item: ImageItem;
  cropState: CropState;
  removeMetadata: boolean;
  resizeEnabled: boolean;
  outputFormat: SupportedMimeType;
  quality: number;
}) =>
  removeMetadata ||
  resizeEnabled ||
  cropState.enabled ||
  outputFormat !== item.originalType ||
  (outputFormat !== "image/png" && quality < 100);

const ImageCompressionTool: React.FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cropPreviewRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<ImageItem[]>([]);
  const livePreviewRef = useRef<LivePreviewState | null>(null);
  const cropDragStateRef = useRef<{
    mode: "move" | "resize";
    startClientX: number;
    startClientY: number;
    startRect: CropRect;
  } | null>(null);
  const [items, setItems] = useState<ImageItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [cropStates, setCropStates] = useState<Record<string, CropState>>({});
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [browserSupport, setBrowserSupport] = useState<BrowserSupportState>({
    canvas: false,
    toBlob: false,
    fileApi: false,
    dragDrop: false,
    createImageBitmap: false,
    workers: false,
    webpExport: false,
  });
  const [quality, setQuality] = useState(DEFAULT_QUALITY);
  const [outputFormat, setOutputFormat] =
    useState<SupportedMimeType>("image/jpeg");
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [targetWidth, setTargetWidth] = useState("");
  const [targetHeight, setTargetHeight] = useState("");
  const [preserveAspectRatio, setPreserveAspectRatio] = useState(true);
  const [removeMetadata, setRemoveMetadata] = useState(true);
  const [livePreview, setLivePreview] = useState<LivePreviewState>({
    url: null,
    ownsUrl: false,
    size: null,
    width: null,
    height: null,
    mimeType: null,
    metadataRemoved: false,
    generating: false,
    error: null,
  });

  useEffect(() => {
    updateMetaTags({
      title:
        "Free Image Compression & Image Conversion Tool | JPG, PNG, WebP | Shashi Sharma",
      description:
        "Free client-side image compression and conversion tool for JPG, PNG and WebP. Compress, resize, convert, strip metadata and download images or ZIP batches directly in the browser.",
      keywords:
        "image compressor, image converter, compress jpg, compress png, compress webp, image resize tool, convert png to jpg, convert webp, batch image compression, client side image tool",
      canonical: `${window.location.origin}/image-compression-tool`,
      ogTitle: "Free Image Compression & Image Conversion Tool",
      ogDescription:
        "Compress, resize and convert JPG, PNG and WebP images entirely in the browser. Batch process multiple files and download results or ZIP archives locally.",
      ogUrl: `${window.location.origin}/image-compression-tool`,
      ogType: "website",
      ogImage: `${window.location.origin}/favicon.png`,
      twitterCard: "summary",
      twitterTitle: "Free Image Compression & Image Conversion Tool",
      twitterDescription:
        "Client-side image compression, resizing, format conversion and ZIP download for static sites and local workflows.",
      twitterImage: `${window.location.origin}/favicon.png`,
      structuredData: JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Free Image Compression Tool",
          applicationCategory: "UtilitiesApplication",
          operatingSystem: "Any",
          url: `${window.location.origin}/image-compression-tool`,
          description:
            "Free client-side image compression and conversion tool with JPG, PNG and WebP support, resizing, metadata stripping and batch ZIP downloads.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          featureList: [
            "JPG PNG WebP compression",
            "Canvas-based format conversion",
            "Optional image resizing",
            "Metadata removal by re-rendering",
            "Batch processing and ZIP download",
            "Client-side privacy with no server upload",
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: compressionFaqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.shashi-sharma.com.np/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Utilities",
              item: "https://www.shashi-sharma.com.np/image-compression-tool",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Image Compression Tool",
              item: "https://www.shashi-sharma.com.np/image-compression-tool",
            },
          ],
        },
      ]),
    });
  }, []);

  useEffect(() => {
    detectBrowserSupport().then((support) => {
      setBrowserSupport(support);
      if (!support.webpExport && outputFormat === "image/webp") {
        setOutputFormat("image/jpeg");
      }
    });
  }, [outputFormat]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    livePreviewRef.current = livePreview;
  }, [livePreview]);

  useEffect(() => {
    if (!items.length) {
      setSelectedItemId(null);
      return;
    }

    if (!selectedItemId || !items.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(items[0].id);
    }
  }, [items, selectedItemId]);

  const selectedItem =
    items.find((item) => item.id === selectedItemId) ?? items[0] ?? null;
  const selectedCropState = selectedItem
    ? (cropStates[selectedItem.id] ?? getDefaultCropState())
    : getDefaultCropState();
  const selectedSourceDimensions = selectedItem
    ? getSourceDimensions(
        selectedItem.originalWidth,
        selectedItem.originalHeight,
        selectedCropState,
      )
    : null;
  const selectedTargetDimensions = selectedItem
    ? getTargetDimensions({
        originalWidth:
          selectedSourceDimensions?.width ?? selectedItem.originalWidth,
        originalHeight:
          selectedSourceDimensions?.height ?? selectedItem.originalHeight,
        resizeEnabled,
        targetWidth,
        targetHeight,
        preserveAspectRatio,
      })
    : null;

  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => {
        revokeUrl(item.originalUrl);
        revokeUrl(item.processedUrl);
      });
      if (livePreviewRef.current?.ownsUrl) {
        revokeUrl(livePreviewRef.current.url ?? undefined);
      }
    };
  }, []);

  const essentialSupportAvailable =
    browserSupport.canvas && browserSupport.toBlob && browserSupport.fileApi;

  const processedItems = items.filter(
    (item) => item.status === "done" && item.processedBlob,
  );
  const totalOriginalSize = items.reduce(
    (sum, item) => sum + item.originalSize,
    0,
  );
  const totalProcessedSize = processedItems.reduce(
    (sum, item) => sum + (item.processedSize ?? 0),
    0,
  );
  const totalReductionPercent =
    totalOriginalSize > 0
      ? ((totalOriginalSize - totalProcessedSize) / totalOriginalSize) * 100
      : 0;

  const supportChips = [
    { label: "Canvas API", ok: browserSupport.canvas },
    { label: "canvas.toBlob()", ok: browserSupport.toBlob },
    { label: "File API", ok: browserSupport.fileApi },
    { label: "Drag & drop", ok: browserSupport.dragDrop },
    { label: "createImageBitmap", ok: browserSupport.createImageBitmap },
    { label: "Web Workers", ok: browserSupport.workers },
    { label: "WebP export", ok: browserSupport.webpExport },
  ];

  const resultSummaryCards = [
    {
      label: "Images queued",
      value: items.length,
      helper: `${processedItems.length} processed`,
      icon: <ImageIcon color="primary" />,
    },
    {
      label: "Original total",
      value: formatBytes(totalOriginalSize),
      helper: items.length ? "Before processing" : "Upload images first",
      icon: <CompareArrowsIcon color="primary" />,
    },
    {
      label: "Processed total",
      value: formatBytes(totalProcessedSize),
      helper: processedItems.length
        ? `${formatPercent(totalReductionPercent)} smaller`
        : "No output yet",
      icon: <AutoFixHighIcon color="primary" />,
    },
    {
      label: "Output format",
      value: getFormatLabel(outputFormat),
      helper: resizeEnabled ? "Resize enabled" : "Keep original size",
      icon: <TuneIcon color="primary" />,
    },
  ];

  const setSelectedCropState = (
    updater: CropState | ((current: CropState) => CropState),
  ) => {
    if (!selectedItem) {
      return;
    }

    setCropStates((current) => {
      const previous = current[selectedItem.id] ?? getDefaultCropState();
      const nextState =
        typeof updater === "function" ? updater(previous) : updater;

      return {
        ...current,
        [selectedItem.id]: {
          ...nextState,
          rect: normalizeCropRect(nextState.rect),
        },
      };
    });
  };

  const renderImageOutput = useCallback(
    async (item: ImageItem) => {
      const cropState = cropStates[item.id] ?? getDefaultCropState();
      const shouldUseCanvas = shouldTransformImage({
        item,
        cropState,
        removeMetadata,
        resizeEnabled,
        outputFormat,
        quality,
      });

      if (!shouldUseCanvas) {
        return {
          blob: item.file,
          width: item.originalWidth,
          height: item.originalHeight,
          mimeType: item.originalType,
          metadataRemoved: false,
        };
      }

      const image = await loadImageElement(item.originalUrl);
      const cropPixels = cropState.enabled
        ? getCropRectInPixels(
            cropState.rect,
            image.naturalWidth,
            image.naturalHeight,
          )
        : {
            x: 0,
            y: 0,
            width: image.naturalWidth,
            height: image.naturalHeight,
          };
      const dimensions = getTargetDimensions({
        originalWidth: cropPixels.width,
        originalHeight: cropPixels.height,
        resizeEnabled,
        targetWidth,
        targetHeight,
        preserveAspectRatio,
      });

      const canvas = document.createElement("canvas");
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas 2D context is not available.");
      }

      context.clearRect(0, 0, dimensions.width, dimensions.height);
      context.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        dimensions.width,
        dimensions.height,
      );

      const blob = await canvasToBlob(
        canvas,
        outputFormat,
        outputFormat === "image/png" ? undefined : quality / 100,
      );

      return {
        blob,
        width: dimensions.width,
        height: dimensions.height,
        mimeType: outputFormat,
        metadataRemoved: true,
      };
    },
    [
      cropStates,
      outputFormat,
      preserveAspectRatio,
      quality,
      removeMetadata,
      resizeEnabled,
      targetHeight,
      targetWidth,
    ],
  );

  useEffect(() => {
    if (!selectedItem) {
      setLivePreview((current) => {
        if (current.ownsUrl) {
          revokeUrl(current.url ?? undefined);
        }
        return {
          url: null,
          ownsUrl: false,
          size: null,
          width: null,
          height: null,
          mimeType: null,
          metadataRemoved: false,
          generating: false,
          error: null,
        };
      });
      return;
    }

    let cancelled = false;

    setLivePreview((current) => ({
      ...current,
      generating: true,
      error: null,
    }));

    const updatePreview = async () => {
      try {
        const result = await renderImageOutput(selectedItem);
        const isOriginalBlob = result.blob === selectedItem.file;
        const nextUrl = isOriginalBlob
          ? selectedItem.originalUrl
          : createObjectUrl(result.blob);

        if (cancelled) {
          if (!isOriginalBlob) {
            revokeUrl(nextUrl);
          }
          return;
        }

        setLivePreview((current) => {
          if (current.ownsUrl) {
            revokeUrl(current.url ?? undefined);
          }

          return {
            url: nextUrl,
            ownsUrl: !isOriginalBlob,
            size: result.blob.size,
            width: result.width,
            height: result.height,
            mimeType: result.mimeType,
            metadataRemoved: result.metadataRemoved,
            generating: false,
            error: null,
          };
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLivePreview((current) => {
          if (current.ownsUrl) {
            revokeUrl(current.url ?? undefined);
          }

          return {
            url: selectedItem.originalUrl,
            ownsUrl: false,
            size: selectedItem.originalSize,
            width: selectedItem.originalWidth,
            height: selectedItem.originalHeight,
            mimeType: selectedItem.originalType,
            metadataRemoved: false,
            generating: false,
            error:
              error instanceof Error
                ? error.message
                : "Unable to build the live preview.",
          };
        });
      }
    };

    void updatePreview();

    return () => {
      cancelled = true;
    };
  }, [renderImageOutput, selectedItem]);

  const addFiles = async (fileList: FileList | File[]) => {
    if (!essentialSupportAvailable) {
      setFeedback(
        "This browser is missing required image processing features.",
      );
      return;
    }

    const incomingFiles = Array.from(fileList).slice(0, MAX_FILES);
    const acceptedFiles = incomingFiles.filter((file) =>
      ACCEPTED_TYPES.includes(file.type as SupportedMimeType),
    );

    if (!acceptedFiles.length) {
      setFeedback("Please select PNG, JPG/JPEG, or WebP image files.");
      return;
    }

    const nextItems = await Promise.all(
      acceptedFiles.map(async (file, index) => {
        const originalUrl = createObjectUrl(file);
        const image = await loadImageElement(originalUrl);

        return {
          id: `${file.name}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          originalUrl,
          originalName: file.name,
          originalType: file.type as SupportedMimeType,
          originalSize: file.size,
          originalWidth: image.naturalWidth,
          originalHeight: image.naturalHeight,
          status: "ready" as ItemStatus,
        };
      }),
    );

    setItems((current) => [...current, ...nextItems]);
    setCropStates((current) => {
      const nextState = { ...current };
      nextItems.forEach((item) => {
        if (!nextState[item.id]) {
          nextState[item.id] = getDefaultCropState();
        }
      });
      return nextState;
    });
    setSelectedItemId((current) => current ?? nextItems[0]?.id ?? null);
    setFeedback(
      `${nextItems.length} image${nextItems.length === 1 ? "" : "s"} added.`,
    );
  };

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files?.length) {
      await addFiles(files);
    }
    event.target.value = "";
  };

  const removeItem = (itemId: string) => {
    setItems((current) => {
      const item = current.find((entry) => entry.id === itemId);
      if (item) {
        revokeUrl(item.originalUrl);
        revokeUrl(item.processedUrl);
      }
      return current.filter((entry) => entry.id !== itemId);
    });
    setCropStates((current) => {
      const nextState = { ...current };
      delete nextState[itemId];
      return nextState;
    });
    setSelectedItemId((current) => (current === itemId ? null : current));
  };

  const clearAll = () => {
    items.forEach((item) => {
      revokeUrl(item.originalUrl);
      revokeUrl(item.processedUrl);
    });
    setItems([]);
    setCropStates({});
    setSelectedItemId(null);
    setFeedback("Image queue cleared.");
  };

  const processOneImage = async (item: ImageItem) => {
    return renderImageOutput(item);
  };

  const processImages = async () => {
    if (!items.length) {
      setFeedback("Upload at least one image first.");
      return;
    }

    if (!essentialSupportAvailable) {
      setFeedback(
        "This browser does not support the required client-side image APIs.",
      );
      return;
    }

    setIsProcessing(true);
    setFeedback(null);

    for (const item of items) {
      setItems((current) =>
        current.map((entry) =>
          entry.id === item.id
            ? { ...entry, status: "processing", error: undefined }
            : entry,
        ),
      );

      try {
        const existingProcessedUrl = items.find(
          (entry) => entry.id === item.id,
        )?.processedUrl;
        revokeUrl(existingProcessedUrl);

        const result = await processOneImage(item);
        const processedUrl = createObjectUrl(result.blob);
        const reductionPercent =
          item.originalSize > 0
            ? ((item.originalSize - result.blob.size) / item.originalSize) * 100
            : 0;

        setItems((current) =>
          current.map((entry) =>
            entry.id === item.id
              ? {
                  ...entry,
                  status: "done",
                  processedBlob: result.blob,
                  processedUrl,
                  processedName: getProcessedFileName(
                    entry.originalName,
                    result.mimeType,
                  ),
                  processedType: result.mimeType,
                  processedSize: result.blob.size,
                  processedWidth: result.width,
                  processedHeight: result.height,
                  reductionPercent,
                  metadataRemoved: result.metadataRemoved,
                  error: undefined,
                }
              : entry,
          ),
        );
      } catch (error) {
        setItems((current) =>
          current.map((entry) =>
            entry.id === item.id
              ? {
                  ...entry,
                  status: "error",
                  error:
                    error instanceof Error
                      ? error.message
                      : "Unable to process this image.",
                }
              : entry,
          ),
        );
      }

      await new Promise<void>((resolve) => {
        window.setTimeout(() => resolve(), 0);
      });
    }

    setIsProcessing(false);
    setFeedback("Image batch processing completed locally in the browser.");
  };

  const downloadZip = async () => {
    if (!processedItems.length) {
      setFeedback("Process images before downloading a ZIP archive.");
      return;
    }

    const zip = new JSZip();
    processedItems.forEach((item) => {
      if (item.processedBlob && item.processedName) {
        zip.file(item.processedName, item.processedBlob);
      }
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    triggerBlobDownload(zipBlob, "compressed-images.zip");
    setFeedback("ZIP archive downloaded.");
  };

  const formatOptions: Array<{
    value: SupportedMimeType;
    label: string;
    disabled?: boolean;
  }> = [
    { value: "image/jpeg", label: "JPG" },
    { value: "image/png", label: "PNG" },
    {
      value: "image/webp",
      label: "WebP",
      disabled: !browserSupport.webpExport,
    },
  ];

  const handleCropPresetChange = (preset: CropAspectPreset) => {
    if (!selectedItem) {
      return;
    }

    setSelectedCropState((current) => ({
      ...current,
      aspectPreset: preset,
      rect: applyAspectToCropRect(
        current.rect,
        preset,
        selectedItem.originalWidth,
        selectedItem.originalHeight,
      ),
    }));
  };

  const updateCropRectFromPointer = (clientX: number, clientY: number) => {
    if (!selectedItem || !cropPreviewRef.current || !cropDragStateRef.current) {
      return;
    }

    const bounds = cropPreviewRef.current.getBoundingClientRect();
    const deltaXPercent =
      ((clientX - cropDragStateRef.current.startClientX) / bounds.width) * 100;
    const deltaYPercent =
      ((clientY - cropDragStateRef.current.startClientY) / bounds.height) * 100;
    const startRect = cropDragStateRef.current.startRect;
    const aspectValue = getCropAspectValue(
      selectedCropState.aspectPreset,
      selectedItem.originalWidth / selectedItem.originalHeight,
    );

    let nextRect: CropRect;

    if (cropDragStateRef.current.mode === "move") {
      nextRect = normalizeCropRect({
        ...startRect,
        x: startRect.x + deltaXPercent,
        y: startRect.y + deltaYPercent,
      });
    } else {
      let nextWidth = clamp(
        startRect.width + deltaXPercent,
        MIN_CROP_PERCENT,
        100 - startRect.x,
      );
      let nextHeight = clamp(
        startRect.height + deltaYPercent,
        MIN_CROP_PERCENT,
        100 - startRect.y,
      );

      if (aspectValue) {
        const startWidthPx = (nextWidth / 100) * selectedItem.originalWidth;
        const nextHeightPx = startWidthPx / aspectValue;
        nextHeight = clamp(
          (nextHeightPx / selectedItem.originalHeight) * 100,
          MIN_CROP_PERCENT,
          100 - startRect.y,
        );

        const adjustedWidthPx =
          (nextHeight / 100) * selectedItem.originalHeight * aspectValue;
        nextWidth = clamp(
          (adjustedWidthPx / selectedItem.originalWidth) * 100,
          MIN_CROP_PERCENT,
          100 - startRect.x,
        );
      }

      nextRect = normalizeCropRect({
        ...startRect,
        width: nextWidth,
        height: nextHeight,
      });
    }

    setSelectedCropState((current) => ({
      ...current,
      rect: nextRect,
    }));
  };

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      updateCropRectFromPointer(event.clientX, event.clientY);
    };

    const handlePointerUp = () => {
      cropDragStateRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  });

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, sm: 3.5 }, flex: 1 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 800, mb: 0.75 }}
            >
              Free Image Compression & Conversion Tool
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 980 }}
            >
              Compress, resize and convert PNG, JPG and WebP images entirely in
              the browser. This static-site-friendly utility uses canvas and
              modern browser APIs only, so images stay on your device while you
              optimize them for websites, blogs and apps.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.5 }}
            >
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label="Client-side only"
              />
              <Chip size="small" variant="outlined" label="PNG, JPG, WebP" />
              <Chip size="small" variant="outlined" label="Batch processing" />
              <Chip size="small" variant="outlined" label="ZIP download" />
              <Chip
                size="small"
                variant="outlined"
                label="Static hosting friendly"
              />
            </Stack>
          </Box>

          {!essentialSupportAvailable && (
            <Alert severity="error">
              Your browser is missing one or more required features for local
              image processing. Canvas, File APIs and `canvas.toBlob()` are
              required.
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            {resultSummaryCards.map((card) => (
              <Card key={card.label} variant="outlined">
                <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Stack spacing={1.2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          backgroundColor: "rgba(145, 189, 58, 0.08)",
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {card.label}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.helper}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0, 1.22fr) minmax(320px, 380px)",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <UploadFileIcon color="primary" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Upload images
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Button
                          component="label"
                          variant="contained"
                          startIcon={<UploadFileIcon />}
                          disabled={!essentialSupportAvailable}
                          size="small"
                        >
                          Choose files
                          <Box
                            component="input"
                            ref={inputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            multiple
                            sx={{ display: "none" }}
                            onChange={handleFileSelection}
                          />
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={clearAll}
                          disabled={!items.length}
                          size="small"
                        >
                          Clear
                        </Button>
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        border: "1px dashed",
                        borderColor: dragActive ? "primary.main" : "divider",
                        borderRadius: 3,
                        p: 1.75,
                        backgroundColor: dragActive
                          ? "rgba(145, 189, 58, 0.08)"
                          : "background.default",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        setDragActive(true);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        setDragActive(false);
                      }}
                      onDrop={async (event) => {
                        event.preventDefault();
                        setDragActive(false);
                        if (event.dataTransfer.files?.length) {
                          await addFiles(event.dataTransfer.files);
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Drag images here or use the button above.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PNG, JPG/JPEG, WebP • max {MAX_FILES} files • fully
                        local processing
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${items.length} queued`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${processedItems.length} processed`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          browserSupport.workers
                            ? "Worker support available"
                            : "Sequential browser processing"
                        }
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CompareArrowsIcon color="primary" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Live preview
                        </Typography>
                      </Stack>
                      {selectedItem && (
                        <Chip
                          size="small"
                          label={selectedItem.status.toUpperCase()}
                          color={
                            selectedItem.status === "done"
                              ? "success"
                              : selectedItem.status === "error"
                                ? "error"
                                : selectedItem.status === "processing"
                                  ? "warning"
                                  : "default"
                          }
                        />
                      )}
                    </Stack>

                    {!selectedItem ? (
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          minHeight: 240,
                          borderRadius: 3,
                          border: "1px dashed",
                          borderColor: dragActive ? "primary.main" : "divider",
                          backgroundColor: dragActive
                            ? "rgba(145, 189, 58, 0.08)"
                            : "background.default",
                          px: 2,
                          textAlign: "center",
                        }}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          setDragActive(true);
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragActive(true);
                        }}
                        onDragLeave={(event) => {
                          event.preventDefault();
                          setDragActive(false);
                        }}
                        onDrop={async (event) => {
                          event.preventDefault();
                          setDragActive(false);
                          if (event.dataTransfer.files?.length) {
                            await addFiles(event.dataTransfer.files);
                          }
                        }}
                      >
                        <Stack spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 999,
                              display: "grid",
                              placeItems: "center",
                              backgroundColor: "rgba(145, 189, 58, 0.08)",
                            }}
                          >
                            <ImageIcon color="primary" sx={{ fontSize: 28 }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Drop images here to start
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            PNG, JPG/JPEG and WebP are processed locally in your
                            browser.
                          </Typography>
                          <Button
                            component="label"
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            disabled={!essentialSupportAvailable}
                          >
                            Choose files
                            <Box
                              component="input"
                              ref={inputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              multiple
                              sx={{ display: "none" }}
                              onChange={handleFileSelection}
                            />
                          </Button>
                          <Typography variant="caption" color="text.secondary">
                            Up to {MAX_FILES} files per batch
                          </Typography>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 280px))",
                            },
                            gap: 1.25,
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: { xs: "100%", sm: 600 },
                            mx: "auto",
                          }}
                        >
                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 3,
                              overflow: "hidden",
                              backgroundColor: "background.default",
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Original{" "}
                                {selectedCropState.enabled
                                  ? "with crop tool"
                                  : "preview"}
                              </Typography>
                            </Box>
                            <Box
                              ref={cropPreviewRef}
                              sx={{
                                width: "100%",
                                aspectRatio: `${selectedItem.originalWidth} / ${selectedItem.originalHeight}`,
                                backgroundColor: "background.paper",
                                position: "relative",
                                overflow: "hidden",
                                maxWidth: 280,
                                mx: "auto",
                              }}
                            >
                              <Box
                                component="img"
                                src={selectedItem.originalUrl}
                                alt={selectedItem.originalName}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                  filter: selectedCropState.enabled
                                    ? "brightness(0.78)"
                                    : "none",
                                }}
                              />
                              {selectedCropState.enabled && (
                                <Box
                                  onPointerDown={(event) => {
                                    event.preventDefault();
                                    cropDragStateRef.current = {
                                      mode: "move",
                                      startClientX: event.clientX,
                                      startClientY: event.clientY,
                                      startRect: selectedCropState.rect,
                                    };
                                  }}
                                  sx={{
                                    position: "absolute",
                                    left: `${selectedCropState.rect.x}%`,
                                    top: `${selectedCropState.rect.y}%`,
                                    width: `${selectedCropState.rect.width}%`,
                                    height: `${selectedCropState.rect.height}%`,
                                    border: "2px solid",
                                    borderColor: "primary.main",
                                    backgroundColor: "rgba(145, 189, 58, 0.15)",
                                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
                                    cursor: "move",
                                  }}
                                >
                                  <Box
                                    onPointerDown={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      cropDragStateRef.current = {
                                        mode: "resize",
                                        startClientX: event.clientX,
                                        startClientY: event.clientY,
                                        startRect: selectedCropState.rect,
                                      };
                                    }}
                                    sx={{
                                      position: "absolute",
                                      right: -8,
                                      bottom: -8,
                                      width: 16,
                                      height: 16,
                                      borderRadius: "50%",
                                      backgroundColor: "primary.main",
                                      border: "2px solid",
                                      borderColor: "background.paper",
                                      cursor: "nwse-resize",
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 3,
                              overflow: "hidden",
                              backgroundColor: "background.default",
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Live output preview
                              </Typography>
                            </Box>
                            {livePreview.url ? (
                              <Box
                                component="img"
                                src={livePreview.url}
                                alt={`${selectedItem.originalName} live preview`}
                                sx={{
                                  width: "100%",
                                  aspectRatio: `${livePreview.width ?? selectedItem.originalWidth} / ${livePreview.height ?? selectedItem.originalHeight}`,
                                  objectFit: "cover",
                                  backgroundColor: "background.paper",
                                  display: "block",
                                  maxWidth: 280,
                                  mx: "auto",
                                }}
                              />
                            ) : (
                              <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  minHeight: 180,
                                  backgroundColor: "background.paper",
                                  px: 2,
                                  textAlign: "center",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {livePreview.generating
                                    ? "Building live preview..."
                                    : "Adjust settings to preview the output instantly."}
                                </Typography>
                              </Stack>
                            )}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "repeat(2, minmax(0, 1fr))",
                              sm: "repeat(4, minmax(0, 1fr))",
                            },
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              backgroundColor: "background.default",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Original
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {formatBytes(selectedItem.originalSize)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              backgroundColor: "background.default",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Output target
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {getFormatLabel(outputFormat)} •{" "}
                              {selectedTargetDimensions?.width ??
                                selectedItem.originalWidth}
                              ×
                              {selectedTargetDimensions?.height ??
                                selectedItem.originalHeight}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              backgroundColor: "background.default",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Latest result
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {livePreview.size
                                ? formatBytes(livePreview.size)
                                : selectedItem.processedSize
                                  ? formatBytes(selectedItem.processedSize)
                                  : "Not processed"}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              backgroundColor: "background.default",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Reduction
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {livePreview.size
                                ? formatPercent(
                                    ((selectedItem.originalSize -
                                      livePreview.size) /
                                      selectedItem.originalSize) *
                                      100,
                                  )
                                : typeof selectedItem.reductionPercent ===
                                    "number"
                                  ? formatPercent(selectedItem.reductionPercent)
                                  : "—"}
                            </Typography>
                          </Box>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={selectedItem.originalName}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${getFormatLabel(selectedItem.originalType)} • ${selectedItem.originalWidth}×${selectedItem.originalHeight}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              removeMetadata
                                ? "Metadata will be stripped"
                                : "Metadata preserved when possible"
                            }
                          />
                          {selectedCropState.enabled && (
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={`Crop ${Math.round(selectedCropState.rect.width)}% × ${Math.round(selectedCropState.rect.height)}%`}
                            />
                          )}
                          {livePreview.width && livePreview.height && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Live ${livePreview.width}×${livePreview.height}`}
                            />
                          )}
                          {selectedItem.metadataRemoved !== undefined && (
                            <Chip
                              size="small"
                              color={
                                selectedItem.metadataRemoved
                                  ? "success"
                                  : "default"
                              }
                              variant="outlined"
                              label={
                                selectedItem.metadataRemoved
                                  ? "Latest export stripped metadata"
                                  : "Latest export kept metadata"
                              }
                            />
                          )}
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.25}
                        >
                          <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            disabled={
                              !selectedItem.processedBlob &&
                              (!livePreview.url || livePreview.generating)
                            }
                            onClick={async () => {
                              if (selectedItem.processedBlob) {
                                // Use processed blob if available
                                const fileName =
                                  selectedItem.processedName ||
                                  getProcessedFileName(
                                    selectedItem.originalName,
                                    selectedItem.processedType ||
                                      selectedItem.originalType,
                                  );
                                triggerBlobDownload(
                                  selectedItem.processedBlob,
                                  fileName,
                                );
                              } else if (
                                livePreview.url &&
                                !livePreview.generating
                              ) {
                                // Fallback to live preview
                                try {
                                  const response = await fetch(livePreview.url);
                                  const blob = await response.blob();
                                  const fileName = getProcessedFileName(
                                    selectedItem.originalName,
                                    livePreview.mimeType ||
                                      selectedItem.originalType,
                                  );
                                  triggerBlobDownload(blob, fileName);
                                } catch (error) {
                                  // Fallback: render and download on demand
                                  try {
                                    const result =
                                      await renderImageOutput(selectedItem);
                                    const fileName = getProcessedFileName(
                                      selectedItem.originalName,
                                      result.mimeType,
                                    );
                                    triggerBlobDownload(result.blob, fileName);
                                  } catch (_err) {
                                    setFeedback(
                                      "Unable to download the image. Please try processing it first.",
                                    );
                                  }
                                }
                              }
                            }}
                          >
                            Download selected
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => removeItem(selectedItem.id)}
                          >
                            Remove selected
                          </Button>
                        </Stack>

                        {livePreview.error && (
                          <Alert severity="warning">{livePreview.error}</Alert>
                        )}

                        {selectedItem.error && (
                          <Alert severity="error">{selectedItem.error}</Alert>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {!!items.length && (
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 1.75 } }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Queue
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            sm: "repeat(4, minmax(0, 1fr))",
                          },
                          gap: 1,
                        }}
                      >
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            onClick={() => setSelectedItemId(item.id)}
                            sx={{
                              border: "1px solid",
                              borderColor:
                                item.id === selectedItem?.id
                                  ? "primary.main"
                                  : "divider",
                              borderRadius: 2,
                              p: 1,
                              cursor: "pointer",
                              backgroundColor:
                                item.id === selectedItem?.id
                                  ? "rgba(145, 189, 58, 0.08)"
                                  : "background.default",
                            }}
                          >
                            <Stack spacing={0.75}>
                              <Box
                                component="img"
                                src={item.processedUrl || item.originalUrl}
                                alt={item.originalName}
                                sx={{
                                  width: "100%",
                                  height: 88,
                                  objectFit: "contain",
                                  borderRadius: 1.5,
                                  backgroundColor: "background.paper",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 1,
                                  overflow: "hidden",
                                }}
                              >
                                {item.originalName}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                useFlexGap
                                flexWrap="wrap"
                              >
                                <Chip
                                  size="small"
                                  label={item.status}
                                  color={
                                    item.status === "done"
                                      ? "success"
                                      : item.status === "error"
                                        ? "error"
                                        : item.status === "processing"
                                          ? "warning"
                                          : "default"
                                  }
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={
                                    item.processedSize
                                      ? formatBytes(item.processedSize)
                                      : formatBytes(item.originalSize)
                                  }
                                />
                              </Stack>
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {feedback && (
                <Alert severity="success" onClose={() => setFeedback(null)}>
                  {feedback}
                </Alert>
              )}
            </Stack>

            <Stack
              spacing={2}
              sx={{
                position: { sm: "sticky" },
                top: { sm: 88 },
                alignSelf: "start",
                maxHeight: { sm: "calc(100vh - 104px)" },
                overflowY: { sm: "auto" },
                pr: { sm: 0.5 },
              }}
            >
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Stack spacing={1.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TuneIcon color="primary" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Tools & properties
                        </Typography>
                      </Stack>
                    </Stack>

                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Quality
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {quality}%
                        </Typography>
                      </Stack>
                      <Slider
                        min={0}
                        max={100}
                        step={1}
                        value={quality}
                        onChange={(_event, value) =>
                          setQuality(value as number)
                        }
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 1.25,
                      }}
                    >
                      <FormControl fullWidth size="small">
                        <InputLabel id="output-format-label">
                          Output format
                        </InputLabel>
                        <Select
                          labelId="output-format-label"
                          label="Output format"
                          value={outputFormat}
                          onChange={(event) =>
                            setOutputFormat(
                              event.target.value as SupportedMimeType,
                            )
                          }
                        >
                          {formatOptions.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              disabled={option.disabled}
                            >
                              {option.label}
                              {option.disabled
                                ? " (not supported in this browser)"
                                : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1,
                          alignItems: "start",
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label="Width"
                          type="number"
                          value={targetWidth}
                          onChange={(event) =>
                            setTargetWidth(event.target.value)
                          }
                          inputProps={{ min: 1 }}
                          disabled={!resizeEnabled}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Height"
                          type="number"
                          value={targetHeight}
                          onChange={(event) =>
                            setTargetHeight(event.target.value)
                          }
                          inputProps={{ min: 1 }}
                          disabled={!resizeEnabled}
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        backgroundColor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={selectedCropState.enabled}
                                onChange={(event) =>
                                  setSelectedCropState((current) => ({
                                    ...current,
                                    enabled: event.target.checked,
                                  }))
                                }
                                disabled={!selectedItem}
                              />
                            }
                            label="Crop selected image"
                          />
                          <Button
                            size="small"
                            variant="text"
                            disabled={!selectedItem}
                            onClick={() =>
                              setSelectedCropState((current) => ({
                                ...current,
                                rect: applyAspectToCropRect(
                                  DEFAULT_CROP_RECT,
                                  current.aspectPreset,
                                  selectedItem?.originalWidth ?? 1,
                                  selectedItem?.originalHeight ?? 1,
                                ),
                              }))
                            }
                          >
                            Reset crop
                          </Button>
                        </Stack>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1,
                          }}
                        >
                          <FormControl
                            fullWidth
                            size="small"
                            disabled={!selectedCropState.enabled}
                          >
                            <InputLabel id="crop-aspect-label">
                              Crop aspect
                            </InputLabel>
                            <Select
                              labelId="crop-aspect-label"
                              label="Crop aspect"
                              value={selectedCropState.aspectPreset}
                              onChange={(event) =>
                                handleCropPresetChange(
                                  event.target.value as CropAspectPreset,
                                )
                              }
                            >
                              <MenuItem value="free">Free</MenuItem>
                              <MenuItem value="original">
                                Original ratio
                              </MenuItem>
                              <MenuItem value="square">Square</MenuItem>
                              <MenuItem value="4:3">4:3</MenuItem>
                              <MenuItem value="16:9">16:9</MenuItem>
                              <MenuItem value="3:4">3:4</MenuItem>
                            </Select>
                          </FormControl>
                          <Box
                            sx={{
                              px: 1.25,
                              py: 0.9,
                              borderRadius: 2,
                              backgroundColor: "background.paper",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Output preview
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {selectedTargetDimensions
                                ? `${selectedTargetDimensions.width}×${selectedTargetDimensions.height}`
                                : "Select an image"}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Drag the crop area on the original preview and use the
                          handle to resize it. Resize dimensions are applied
                          after the crop.
                        </Typography>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 0.25,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={resizeEnabled}
                            onChange={(event) =>
                              setResizeEnabled(event.target.checked)
                            }
                          />
                        }
                        label="Resize before export"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={removeMetadata}
                            onChange={(event) =>
                              setRemoveMetadata(event.target.checked)
                            }
                          />
                        }
                        label="Strip metadata"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preserveAspectRatio}
                            onChange={(event) =>
                              setPreserveAspectRatio(event.target.checked)
                            }
                            disabled={!resizeEnabled}
                          />
                        }
                        label="Preserve aspect ratio"
                      />
                    </Box>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AutoFixHighIcon />}
                        onClick={processImages}
                        disabled={
                          !items.length ||
                          isProcessing ||
                          !essentialSupportAvailable
                        }
                      >
                        {isProcessing ? "Processing..." : "Process images"}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ArchiveIcon />}
                        onClick={downloadZip}
                        disabled={!processedItems.length}
                      >
                        Download ZIP
                      </Button>
                    </Stack>

                    {isProcessing && <LinearProgress />}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PrivacyTipIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Privacy & support checks
                      </Typography>
                    </Stack>
                    <Alert severity="info">
                      Images never leave your device. Compression, resizing,
                      conversion and ZIP creation run entirely in the browser
                      using local APIs only.
                    </Alert>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {supportChips.map((chip) => (
                        <Chip
                          key={chip.label}
                          size="small"
                          color={chip.ok ? "success" : "default"}
                          variant={chip.ok ? "filled" : "outlined"}
                          label={`${chip.label}: ${chip.ok ? "supported" : "limited"}`}
                        />
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Sequential processing is used by default to reduce memory
                      spikes and keep the UI responsive on large batches.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Tips for best results
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Use JPG for photos and screenshots when file size
                      matters most.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Use PNG for graphics that need transparency or lossless
                      export.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Use WebP when the browser support check says export is
                      available.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Resize large uploads to the display size you actually
                      need on the site.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Re-render through canvas to strip metadata before
                      sharing publicly.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>

          <Box component="section">
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 1 }}
                    >
                      Client-side image compression for static websites and
                      modern workflows
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This tool is designed for static websites and
                      privacy-focused image optimization. It works well for
                      Cloudflare Pages, Netlify, GitHub Pages and other
                      frontend-only deployments where you want image
                      compression, resizing and conversion without a backend
                      server.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Popular use cases
                      </Typography>
                      <Stack spacing={1}>
                        {compressionUseCases.map((item) => (
                          <Typography
                            key={item}
                            variant="body2"
                            color="text.secondary"
                          >
                            • {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Why this image tool is useful
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          • Runs fully in the browser with no image upload to
                          servers.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Supports before/after previews, size comparison and
                          percentage reduction.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Handles multiple images sequentially for safer
                          client-side batch processing.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Converts formats, resizes assets and removes
                          metadata using the Canvas API.
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 2 }}
                    >
                      Frequently asked questions
                    </Typography>
                    <Stack spacing={2}>
                      {compressionFaqItems.map((item) => (
                        <Box key={item.question}>
                          <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                          >
                            {item.question}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.answer}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Container>

      <Footer />
    </Box>
  );
};

export default ImageCompressionTool;
